import React, {
  createContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { get, pick, set, find } from 'lodash-es';

const ALWAYS = () => true;

const attributes = [
  { name: 'id' },
  { name: 'guid' },
  { name: 'operationName', required: ALWAYS },
  { name: 'organizationId', required: ALWAYS },
  { name: 'organization' },
  { name: 'slackWebHook' },
  { name: 'contractTypes', default: [] },
  { name: 'isActive', default: true },
  { name: 'officePhone' },
  { name: 'addressLine1' },
  { name: 'addressLine2' },
  { name: 'city' },
  { name: 'state' },
  { name: 'zip' },
  { name: 'sub' },
];

const emptyOperation = attributes.reduce(
  (memo, attribute) => set(memo, attribute.name, attribute.default || ''),
  {},
);

const Context = createContext();

export function Provider(props) {
  const { handleSave, handleClose, isAdmin } = props;
  const [isLoading, setLoading] = useState(true);
  const [operation, setOperation] = useState({
    ...emptyOperation,
    ...pick(
      props.operation,
      attributes.map((a) => a.name),
    ),
  });

  const [error, setError] = useState();
  const isNew = useMemo(() => !operation.guid, [operation.guid]);
  const organizations = useMemo(
    () =>
      isAdmin
        ? props.organizations
        : props.organizations.filter((org) => !org.isDefault),
    [props.organizations, isAdmin],
  );

  const isOrganizationActive = useMemo(() => {
    const selectedOrganization = organizations.find(
      (org) => org.id === operation.organizationId,
    );

    return selectedOrganization?.isActive;
  }, [operation.organizationId, organizations]);

  const updateOperation = useCallback(({ name, value }) => {
    setOperation((current) => ({
      ...current,
      [name]: value,
    }));
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleError = useCallback((e) => {
    setError(e);
  }, []);

  const hasValue = useCallback(
    (attribute) => {
      const value = get(operation, attribute);
      if (typeof value === 'number') return true;
      if (typeof value === 'string') return Boolean(value.trim());
      if (Array.isArray(value)) return value.length > 0;
      return false;
    },
    [operation],
  );

  const canSave = useMemo(
    () =>
      attributes.every((attribute) =>
        attribute.required?.(operation) ? hasValue(attribute.name) : true,
      ),
    [hasValue, operation],
  );

  useEffect(() => {
    if (operation.organizationId) {
      setOperation((prevRecord) => ({
        ...prevRecord,
        isActive: isOrganizationActive ? prevRecord.isActive : false,
      }));
    }
    if (!operation.organizationId) {
      setOperation((prevRecord) => ({
        ...prevRecord,
        organizationId:
          find(organizations, { isDefault: true })?.id || organizations[0]?.id,
      }));
    }
  }, [operation.organizationId, organizations, isOrganizationActive]);

  const context = {
    canSave,
    clearError,
    error,
    handleClose,
    handleError,
    handleSave,
    isLoading,
    isNew,
    organizations,
    setError,
    setLoading,
    updateOperation,
    operation,
    isAdmin,
    isOrganizationActive,
  };

  return <Context.Provider value={context}>{props.children}</Context.Provider>;
}

export function useContext() {
  return React.useContext(Context);
}
